import {
  errors,
  MapConst,
  MIN_TRACKING_POINTS_FOR_POLYLINE,
  TRACKING_POINT_DECIMATION_BY_MAX_ZOOM,
  TRACKING_RENDER_COORDS_PER_POINT,
  TRACKING_RENDER_PACKED_LAT_OFFSET,
  TRACKING_RENDER_PACKED_LNG_OFFSET,
} from "../../constants";
import { TrackingSegmentStyleEnum } from "../../enums";
import {
  ApiException,
  ILocationInfoBase,
  IRideRoute,
  ITrackingInfoDto,
  ITrackingRenderState,
  ITrackingStoreUpdate,
  ITrackingStoreUpdateOptions,
} from "../../models";
import { TLatLngExpression, TLatLngTuple } from "../../models/map.models";
import {
  calculateDateDifference,
  dateToComparableNumber,
} from "../date.helpers";
import {
  normalizeAndSortLocations,
  normalizeTrackingPoints,
} from "../maps.helpers";

export interface IMergedTrackingPolyline {
  positions: TLatLngExpression[];
  lineColor: string;
  dashArray?: string;
}

const sortTrackingDtos = (
  dtos: ITrackingInfoDto[],
): Required<ITrackingInfoDto>[] =>
  [...normalizeTrackingPoints(dtos)].sort(
    (a, b) =>
      dateToComparableNumber(a.insDate) - dateToComparableNumber(b.insDate),
  );

const segmentStyleBetween = ({
  startTime,
  endTime,
}: {
  startTime: TDateISO;
  endTime: TDateISO;
}): TrackingSegmentStyleEnum => {
  const minutes = calculateDateDifference(endTime, startTime, "minute");

  return minutes < MapConst.MINUTES_LIMIT_SOLID_LINE
    ? TrackingSegmentStyleEnum.Solid
    : TrackingSegmentStyleEnum.Dashed;
};

const getLastSortedTrackingDto = (
  dtos: ITrackingInfoDto[],
): Required<ITrackingInfoDto> | undefined => {
  const sorted = sortTrackingDtos(dtos);

  return sorted[sorted.length - 1];
};

/** Trackings are append-only — unchanged when list length and last point id match. */
export const areAppendOnlyTrackingDtosUnchanged = ({
  prev,
  incoming,
}: {
  prev: ITrackingInfoDto[] | undefined;
  incoming: ITrackingInfoDto[];
}): boolean => {
  if (!incoming.length) {
    return !prev?.length;
  }

  if (!prev?.length) {
    return false;
  }

  const prevSorted = sortTrackingDtos(prev);
  const incomingSorted = sortTrackingDtos(incoming);
  const prevLast = prevSorted[prevSorted.length - 1];
  const incomingLast = incomingSorted[incomingSorted.length - 1];

  return (
    prevSorted.length === incomingSorted.length &&
    prevLast.id === incomingLast.id
  );
};

/** Merges poll snapshot with stored DTOs — appends when ids grow, replaces on full snapshot. */
export const mergeTrackingDtos = ({
  prev,
  incoming,
}: {
  prev: ITrackingInfoDto[] | undefined;
  incoming: ITrackingInfoDto[];
}): ITrackingInfoDto[] => {
  if (!incoming.length) {
    return prev ?? [];
  }

  const normalizedIncoming = sortTrackingDtos(incoming);

  if (!prev?.length) {
    return normalizedIncoming;
  }

  if (
    areAppendOnlyTrackingDtosUnchanged({
      prev,
      incoming: normalizedIncoming,
    })
  ) {
    return prev;
  }

  const prevLastId = getLastSortedTrackingDto(prev)?.id ?? 0;
  const appended = normalizedIncoming.filter(d => d.id > prevLastId);

  if (
    appended.length === 0 ||
    (appended.length < normalizedIncoming.length &&
      normalizedIncoming[0].id <= prevLastId)
  ) {
    return normalizedIncoming;
  }

  return sortTrackingDtos([...prev, ...appended]);
};

/** Replace store with the latest GPS point (live-map simple / last-only polling). */
export const mergeTrackingDtosLastOnly = ({
  prev,
  incoming,
}: {
  prev: ITrackingInfoDto[] | undefined;
  incoming: ITrackingInfoDto[];
}): ITrackingInfoDto[] => {
  if (!incoming.length) {
    return prev ?? [];
  }

  const sorted = sortTrackingDtos(incoming);
  const lastIncoming = sorted[sorted.length - 1];

  if (!prev?.length) {
    return [lastIncoming];
  }

  const prevLast = getLastSortedTrackingDto(prev);

  if (prev.length === 1 && prevLast?.id === lastIncoming.id) {
    return prev;
  }

  return [lastIncoming];
};

export const buildTrackingRenderStateFromDtos = ({
  rideId,
  trackingDtos,
}: {
  rideId: number;
  trackingDtos: ITrackingInfoDto[];
}): ITrackingRenderState | undefined => {
  const sorted = sortTrackingDtos(trackingDtos);

  if (!sorted.length) {
    return undefined;
  }

  const pointCount = sorted.length;
  const latLngs = new Float32Array(
    pointCount * TRACKING_RENDER_COORDS_PER_POINT,
  );
  const timestamps: TDateISO[] = new Array(pointCount);
  const speeds: (number | undefined)[] = new Array(pointCount);
  const segmentStyles = new Uint8Array(Math.max(0, pointCount - 1));
  const pointDetour = new Uint8Array(pointCount);

  sorted.forEach((dto, index) => {
    const packedBaseIndex = index * TRACKING_RENDER_COORDS_PER_POINT;
    latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LAT_OFFSET] = dto.latitude;
    latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LNG_OFFSET] =
      dto.longitude;
    timestamps[index] = dto.insDate;
    speeds[index] = dto.speed;
    pointDetour[index] = dto.inDetour ? 1 : 0;

    if (index > 0) {
      segmentStyles[index - 1] = segmentStyleBetween({
        startTime: sorted[index - 1].insDate,
        endTime: dto.insDate,
      });
    }
  });

  const lastDto = sorted[pointCount - 1];

  const renderState: ITrackingRenderState = {
    rideId,
    latLngs,
    timestamps,
    speeds,
    segmentStyles,
    pointDetour,
    lastDtoId: lastDto.id,
    pointCount,
    version: 1,
    carTrackingInfo: {
      rideId,
      trackingLastUpdate: lastDto.insDate,
    },
  };
  return renderState;
};

/** Prefer precomputed `trackingRenderState`; otherwise build from route locations (legacy/fallback). */
export const resolveTrackingRenderState = (
  route: IRideRoute,
): ITrackingRenderState | undefined => {
  if (route.trackingRenderState?.pointCount) {
    return route.trackingRenderState;
  }

  const locations = route.locations;

  if (!locations?.length) {
    return undefined;
  }

  const normalized = normalizeAndSortLocations(locations);

  if (!normalized.length) {
    return undefined;
  }

  const pseudoDtos = normalized.map((location, index) => ({
    id: index + 1,
    rideId: route.rideId,
    insDate: location.arrivalTime,
    latitude: location.latitude,
    longitude: location.longitude,
  }));

  return buildTrackingRenderStateFromDtos({
    rideId: route.rideId ?? 0,
    trackingDtos: pseudoDtos,
  });
};

const appendDtosToRenderState = ({
  prev,
  newDtos,
}: {
  prev: ITrackingRenderState;
  newDtos: Required<ITrackingInfoDto>[];
}): ITrackingRenderState => {
  const sortedNew = sortTrackingDtos(newDtos);
  const addedCount = sortedNew.length;
  const nextPointCount = prev.pointCount + addedCount;
  const latLngs = new Float32Array(
    nextPointCount * TRACKING_RENDER_COORDS_PER_POINT,
  );
  latLngs.set(prev.latLngs);

  const timestamps = [...prev.timestamps];
  const speeds = [...prev.speeds];
  const segmentStyles = new Uint8Array(Math.max(0, nextPointCount - 1));
  segmentStyles.set(prev.segmentStyles);
  const pointDetour = new Uint8Array(nextPointCount);
  pointDetour.set(prev.pointDetour);

  let writeIndex = prev.pointCount;

  sortedNew.forEach(dto => {
    const pointIndex = writeIndex;
    const packedBaseIndex = pointIndex * TRACKING_RENDER_COORDS_PER_POINT;
    latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LAT_OFFSET] = dto.latitude;
    latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LNG_OFFSET] =
      dto.longitude;
    timestamps[pointIndex] = dto.insDate;
    speeds[pointIndex] = dto.speed;
    pointDetour[pointIndex] = dto.inDetour ? 1 : 0;

    if (pointIndex > 0) {
      segmentStyles[pointIndex - 1] = segmentStyleBetween({
        startTime: timestamps[pointIndex - 1],
        endTime: dto.insDate,
      });
    }

    writeIndex += 1;
  });

  const lastDto = sortedNew[addedCount - 1];

  return {
    ...prev,
    latLngs,
    timestamps,
    speeds,
    segmentStyles,
    pointDetour,
    lastDtoId: lastDto.id,
    pointCount: nextPointCount,
    version: prev.version + 1,
    carTrackingInfo: {
      rideId: prev.rideId,
      trackingLastUpdate: lastDto.insDate,
    },
  };
};

export const syncTrackingRenderState = ({
  prev,
  mergedDtos,
  rideId,
}: {
  prev: ITrackingRenderState | undefined;
  mergedDtos: ITrackingInfoDto[];
  rideId: number;
}): ITrackingRenderState | undefined => {
  const sorted = sortTrackingDtos(mergedDtos);

  if (!sorted.length) {
    return prev;
  }

  if (!prev) {
    return buildTrackingRenderStateFromDtos({ rideId, trackingDtos: sorted });
  }

  const lastSorted = sorted[sorted.length - 1];

  if (lastSorted.id === prev.lastDtoId && sorted.length === prev.pointCount) {
    return prev;
  }

  const newDtos = sorted.filter(d => d.id > prev.lastDtoId);

  if (newDtos.length > 0) {
    const isSnapshotReplace =
      newDtos.length < sorted.length && sorted.length === prev.pointCount;

    if (isSnapshotReplace) {
      return buildTrackingRenderStateFromDtos({ rideId, trackingDtos: sorted });
    }

    return appendDtosToRenderState({ prev, newDtos });
  }

  return buildTrackingRenderStateFromDtos({ rideId, trackingDtos: sorted });
};

/** Single entry for Recoil store updates (raw DTOs + render state). */
export const computeTrackingStoreUpdate = ({
  rideId,
  incomingDtos,
  prevDtos,
  prevRender,
  options,
}: {
  rideId: number;
  incomingDtos: ITrackingInfoDto[];
  prevDtos: ITrackingInfoDto[] | undefined;
  prevRender: ITrackingRenderState | undefined;
  options?: ITrackingStoreUpdateOptions;
}): ITrackingStoreUpdate => {
  const mergedDtos = options?.lastTrackingOnly
    ? mergeTrackingDtosLastOnly({ prev: prevDtos, incoming: incomingDtos })
    : mergeTrackingDtos({ prev: prevDtos, incoming: incomingDtos });
  const dtosChanged = mergedDtos !== prevDtos;

  if (!mergedDtos.length) {
    const update: ITrackingStoreUpdate = {
      mergedDtos,
      renderState: prevRender,
      changed: false,
    };
    return update;
  }

  let renderState: ITrackingRenderState | undefined;

  if (options?.lastTrackingOnly) {
    renderState = dtosChanged
      ? buildTrackingRenderStateFromDtos({ rideId, trackingDtos: mergedDtos })
      : prevRender;
  } else {
    renderState = syncTrackingRenderState({
      prev: prevRender,
      mergedDtos,
      rideId,
    });
  }

  const renderChanged = renderState !== prevRender;

  const update: ITrackingStoreUpdate = {
    mergedDtos,
    renderState,
    changed: dtosChanged || renderChanged,
  };
  return update;
};

export const renderStateToLocations = (
  state: ITrackingRenderState,
): Required<ILocationInfoBase>[] => {
  const locations: Required<ILocationInfoBase>[] = [];

  for (let i = 0; i < state.pointCount; i += 1) {
    const packedBaseIndex = i * TRACKING_RENDER_COORDS_PER_POINT;
    locations.push({
      latitude:
        state.latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LAT_OFFSET],
      longitude:
        state.latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LNG_OFFSET],
      arrivalTime: state.timestamps[i],
    });
  }

  return locations;
};

export const getLatLngFromRenderState = (
  state: ITrackingRenderState,
  pointIndex: number,
): TLatLngTuple => {
  const packedBaseIndex = pointIndex * TRACKING_RENDER_COORDS_PER_POINT;

  return [
    state.latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LAT_OFFSET],
    state.latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LNG_OFFSET],
  ];
};

export const getTrackingPointDecimationStep = (zoom: number): number => {
  for (const rule of TRACKING_POINT_DECIMATION_BY_MAX_ZOOM) {
    if (zoom <= rule.maxZoom) {
      return rule.step;
    }
  }

  return 1;
};

/** Point indices for intermediate markers (excludes first and last — car uses last). */
export const getDecimatedTrackingMarkerIndices = ({
  pointCount,
  zoom,
  minZoom,
}: {
  pointCount: number;
  zoom: number;
  /** Below this zoom no dots are drawn at all. Unset = always drawn, only thinned by the decimation step. */
  minZoom?: number;
}): number[] => {
  // Below `minZoom` the dots read as a dotted line rather than as hoverable GPS samples, which is their only
  // purpose — and they are the most expensive thing on a crowded map. Left to the caller (like the trail
  // arrows' own gate) because a single-ride view has no reason to pay it: 50 dots cost nothing there.
  if (
    pointCount <= MIN_TRACKING_POINTS_FOR_POLYLINE ||
    (minZoom !== undefined && zoom < minZoom)
  ) {
    return [];
  }

  const step = getTrackingPointDecimationStep(zoom);
  const lastMarkerIndex = pointCount - MIN_TRACKING_POINTS_FOR_POLYLINE;
  const indices: number[] = [];

  for (let i = 1; i <= lastMarkerIndex; i += step) {
    indices.push(i);
  }

  if (indices.at(-1) !== lastMarkerIndex && step > 1) {
    indices.push(lastMarkerIndex);
  }

  return indices;
};

/** A trail segment (between points i and i + 1) is "in detour" when BOTH its endpoints were off-route. */
const isDetourSegment = (
  state: ITrackingRenderState,
  segmentIndex: number,
): boolean =>
  state.pointDetour[segmentIndex] === 1 &&
  state.pointDetour[segmentIndex + 1] === 1;

/**
 * Resolves the rendered line style for a merged trail run, mirrored by the TrackingTrailLegend swatches:
 * - No signal: a >2-min gap is an interpolated straight line (no real GPS), so it reads as grey, sparse
 *   dots. This takes precedence over detour — we can't know the real path (on- or off-route) during a gap.
 * - Detour: off-route runs are red.
 * - Real GPS: everything else is the planned-route color (dashed, parallel to the solid planned route).
 */
export interface IMergedTrailSegmentStyle {
  lineColor: string;
  dashArray: string;
}

export const resolveMergedTrailSegmentStyle = ({
  isGap,
  isDetour,
  routeColor,
}: {
  isGap: boolean;
  isDetour: boolean;
  routeColor: string;
}): IMergedTrailSegmentStyle => {
  if (isGap) {
    const style: IMergedTrailSegmentStyle = {
      lineColor: MapConst.DASHED_LINE_COLOR,
      dashArray: MapConst.DASHED_LINE_OPTIONS,
    };
    return style;
  }

  if (isDetour) {
    const style: IMergedTrailSegmentStyle = {
      lineColor: MapConst.GPS_TRAIL_DETOUR_COLOR,
      dashArray: MapConst.GPS_TRAIL_DASH_OPTIONS,
    };
    return style;
  }

  const style: IMergedTrailSegmentStyle = {
    lineColor: routeColor,
    dashArray: MapConst.GPS_TRAIL_DASH_OPTIONS,
  };
  return style;
};

/** Merges consecutive segments that share both dash-style and detour-state into one polyline each. */
export const createMergedPolylinesFromRenderState = ({
  state,
  routeColor,
}: {
  state: ITrackingRenderState;
  routeColor: string;
}): IMergedTrackingPolyline[] => {
  if (state.pointCount < MIN_TRACKING_POINTS_FOR_POLYLINE) {
    return [];
  }

  const lines: IMergedTrackingPolyline[] = [];
  let currentStyle = state.segmentStyles[0];
  let isCurrentDetour = isDetourSegment(state, 0);
  let currentPositions: TLatLngExpression[] = [
    getLatLngFromRenderState(state, 0),
    getLatLngFromRenderState(state, 1),
  ];

  const flush = (): void => {
    if (currentPositions.length < MIN_TRACKING_POINTS_FOR_POLYLINE) {
      return;
    }

    const { lineColor, dashArray } = resolveMergedTrailSegmentStyle({
      isGap: currentStyle === TrackingSegmentStyleEnum.Dashed,
      isDetour: isCurrentDetour,
      routeColor,
    });
    lines.push({ positions: currentPositions, lineColor, dashArray });
  };

  for (
    let segmentIndex = 1;
    segmentIndex < state.segmentStyles.length;
    segmentIndex += 1
  ) {
    const style = state.segmentStyles[segmentIndex];
    const isDetour = isDetourSegment(state, segmentIndex);

    if (style === currentStyle && isDetour === isCurrentDetour) {
      currentPositions.push(getLatLngFromRenderState(state, segmentIndex + 1));
      continue;
    }

    flush();
    currentStyle = style;
    isCurrentDetour = isDetour;
    currentPositions = [
      getLatLngFromRenderState(state, segmentIndex),
      getLatLngFromRenderState(state, segmentIndex + 1),
    ];
  }

  flush();

  return lines;
};

/** Stable segment layout key — same when only coordinates change (setLatLngs in place). */
export const mergedTrackingPolylinesStructureKey = (
  lines: IMergedTrackingPolyline[],
): string =>
  lines.map(line => `${line.dashArray ?? "solid"}:${line.lineColor}`).join("|");

export const trackingRenderGeometryKey = (
  state: ITrackingRenderState | undefined,
): string => {
  if (!state?.pointCount) {
    return "";
  }

  const lastIndex = state.pointCount - 1;

  const packedBaseIndex = lastIndex * TRACKING_RENDER_COORDS_PER_POINT;

  // pointCount must be part of the key: switching simple (last point only) → full (whole trail) keeps the same
  // version + last point, so without pointCount the memo/key would swallow the trail growing from 1 → N points.
  return `${state.version}:${state.pointCount}:${state.lastDtoId}:${state.latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LAT_OFFSET]}:${state.latLngs[packedBaseIndex + TRACKING_RENDER_PACKED_LNG_OFFSET]}`;
};

/**
 * `POST trackings/:id/tracking-info` answers 404 `errors.missingRide` once the ride is archived or
 * deleted. Background fetches (edit drawer trail, live-map poll of the selected ride, investigation)
 * can still fire for that id, so this one case is expected and must not reach the operator as a
 * toast. The batch endpoint drops missing ids itself and never returns it.
 */
export const isTrackingMissingRideError = (error: unknown): boolean =>
  error instanceof ApiException &&
  error.translationKey === errors.ride.missingRide;
